import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Users,
  Mail,
  Phone,
  Award,
  BookOpen,
  ChevronRight,
  Globe,
  FileCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import PublicFooter from '../components/PublicFooter';
import SEO, { PageSEO } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function PublicNav() {
  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/public" className="flex items-center gap-2">
            <div className="flex items-center justify-center w-12 h-12 bg-[#1E3A5F] rounded-lg">
              <svg viewBox="0 0 40 40" className="w-8 h-8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 4L8 10V18C8 26.84 13.16 34.92 20 37C26.84 34.92 32 26.84 32 18V10L20 4Z" fill="#0EA5E9"/>
                <path d="M16 18L19 21L25 15" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold text-[#1E3A5F] leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                DealLab
              </span>
              <span className="text-xs text-sky-600 font-medium -mt-0.5">CE Education</span>
            </div>
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <Link to="/public/courses" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
              Courses
            </Link>
            <Link to="/public/schedule" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
              Schedule
            </Link>
            <Link to="/public/instructors" className="text-[#1E3A5F] font-medium">
              Instructors
            </Link>
            <Link to="/public/sponsors" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
              Become a Sponsor
            </Link>
          </div>
          
          <Link to="/public/request">
            <Button className="bg-sky-500 hover:bg-sky-600 text-white">
              Book for Your Team
            </Button>
          </Link>
        </div>
      </div>
    </nav>
  );
}

export default function PublicInstructorsPage() {
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInstructors();
  }, []);

  const fetchInstructors = async () => {
    try {
      const response = await axios.get(`${API_URL}/public/instructors`);
      setInstructors(response.data);
    } catch (error) {
      console.error('Error fetching instructors:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO {...PageSEO.instructors} />
      <PublicNav />
      
      {/* Header */}
      <section className="bg-[#1E3A5F] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Our Instructors
          </h1>
          <p className="text-sky-200 text-lg max-w-2xl">
            Learn from experienced real estate professionals who bring real-world 
            knowledge and expertise to every course.
          </p>
        </div>
      </section>
      
      {/* Instructors Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-slate-500 mt-4">Loading instructors...</p>
            </div>
          ) : instructors.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">No Instructors Listed</h3>
                <p className="text-slate-500">
                  Check back soon to meet our expert instructors.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {instructors.map((instructor) => (
                <Card 
                  key={instructor.id} 
                  className="border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
                  data-testid={`instructor-card-${instructor.id}`}
                >
                  <CardContent className="pt-8 pb-6">
                    <div className="flex flex-col items-center text-center">
                      {/* Photo or Avatar */}
                      <div className="mb-6">
                        {instructor.photo_url ? (
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${instructor.photo_url}`}
                            alt={instructor.name}
                            className="w-48 h-48 rounded-full object-cover border-4 border-slate-100 shadow-lg"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div 
                          className={`w-48 h-48 rounded-full bg-gradient-to-br from-[#1E3A5F] to-sky-600 flex items-center justify-center text-white text-5xl font-bold shadow-lg ${instructor.photo_url ? 'hidden' : ''}`}
                        >
                          {getInitials(instructor.name)}
                        </div>
                      </div>
                      
                      {/* Name and Title */}
                      <Link 
                        to={`/public/instructors/${instructor.id}`}
                        className="text-2xl font-semibold text-[#1E3A5F] mb-1 hover:text-sky-600 transition-colors"
                        data-testid={`instructor-name-link-${instructor.id}`}
                      >
                        {instructor.name}
                      </Link>
                      {instructor.title && (
                        <p className="text-slate-500 text-base mb-3">
                          {instructor.title}
                        </p>
                      )}
                      
                      {/* Specialties */}
                      {instructor.specialties && instructor.specialties.length > 0 && (
                        <div className="flex flex-wrap justify-center gap-2 mb-4">
                          {instructor.specialties.slice(0, 3).map((specialty, idx) => (
                            <span 
                              key={idx}
                              className="px-3 py-1 bg-sky-100 text-sky-700 text-sm rounded-full"
                            >
                              {specialty}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {/* Bio */}
                      {instructor.bio && (
                        <p className="text-slate-600 text-sm line-clamp-4 mb-4">
                          {instructor.bio}
                        </p>
                      )}
                      
                      {/* Contact Info */}
                      <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                        {instructor.email && (
                          <a 
                            href={`mailto:${instructor.email}`}
                            className="flex items-center gap-1 hover:text-sky-600 transition-colors"
                          >
                            <Mail className="w-4 h-4" />
                            <span className="sr-only">Email</span>
                          </a>
                        )}
                        {instructor.phone && (
                          <a 
                            href={`tel:${instructor.phone}`}
                            className="flex items-center gap-1 hover:text-sky-600 transition-colors"
                          >
                            <Phone className="w-4 h-4" />
                            <span className="sr-only">Phone</span>
                          </a>
                        )}
                        {instructor.website && (
                          <a 
                            href={instructor.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 hover:text-sky-600 transition-colors"
                          >
                            <Globe className="w-4 h-4" />
                            <span className="sr-only">Website</span>
                          </a>
                        )}
                      </div>
                      
                      {/* Certification Button */}
                      {instructor.certification_pdf_url && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
                          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${instructor.certification_pdf_url}`, '_blank')}
                          data-testid={`certification-btn-${instructor.id}`}
                        >
                          <FileCheck className="w-4 h-4 mr-2" />
                          View PSI Certification
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
      
      {/* Why Our Instructors */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1E3A5F] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Why Learn From Our Instructors?
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our instructors are carefully selected for their expertise and ability to deliver 
              engaging, practical continuing education.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-sky-600" />
              </div>
              <h3 className="font-semibold text-[#1E3A5F] mb-2">Industry Experts</h3>
              <p className="text-slate-600 text-sm">
                Active professionals with years of hands-on experience in real estate, 
                lending, and investments.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-[#1E3A5F] mb-2">Engaging Teaching</h3>
              <p className="text-slate-600 text-sm">
                Dynamic presenters who make complex topics accessible and keep 
                students engaged throughout the course.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-[#1E3A5F] mb-2">Practical Focus</h3>
              <p className="text-slate-600 text-sm">
                Real-world case studies and examples you can apply immediately 
                in your business.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="bg-[#1E3A5F] text-white py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Learn from the Experts?
          </h2>
          <p className="text-sky-200 mb-6">
            Browse our course catalog or view upcoming classes to get started.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/public/courses">
              <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white">
                View Courses <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <Link to="/public/schedule">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                See Schedule
              </Button>
            </Link>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <PublicFooter />
    </div>
  );
}
